import { useEffect, useRef, useState, type DragEvent, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

type Props = {
  /** Called with the dropped files that pass `accept`; empty drops are ignored. */
  onFiles: (files: File[]) => void;
  /** Same syntax as `<input accept>`: "image/*,application/pdf". Non-matching files are skipped. */
  accept?: string;
  /** Only the first matching file is passed. */
  multiple?: boolean;
  disabled?: boolean;
  className?: string;
  children: ReactNode;
};

/**
 * Wraps any upload area so files can be dropped straight from the desktop
 * (Strahinja, 2026-09-10: "na svakom delu gde se nešto prilaže"). The children
 * keep their buttons; the zone only adds the drag highlight, the "Pusti ovde"
 * overlay and the drop handling. A drag counter survives the dragenter /
 * dragleave pairs fired by nested elements.
 */
export function FileDropZone({ onFiles, accept, multiple = true, disabled = false, className, children }: Props) {
  const { t } = useTranslation();
  const [over, setOver] = useState(false);
  const depth = useRef(0);

  function hasFiles(e: DragEvent) {
    return Array.from(e.dataTransfer?.types ?? []).includes("Files");
  }

  function onDragEnter(e: DragEvent<HTMLDivElement>) {
    if (disabled || !hasFiles(e)) return;
    e.preventDefault();
    depth.current += 1;
    setOver(true);
  }

  function onDragOver(e: DragEvent<HTMLDivElement>) {
    if (disabled || !hasFiles(e)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  }

  function onDragLeave(e: DragEvent<HTMLDivElement>) {
    if (disabled || !hasFiles(e)) return;
    depth.current = Math.max(0, depth.current - 1);
    if (depth.current === 0) setOver(false);
  }

  function onDrop(e: DragEvent<HTMLDivElement>) {
    if (disabled || !hasFiles(e)) return;
    e.preventDefault();
    depth.current = 0;
    setOver(false);
    const all = Array.from(e.dataTransfer.files ?? []);
    const ok = all.filter((f) => matchesAccept(f, accept));
    const picked = multiple ? ok : ok.slice(0, 1);
    if (picked.length > 0) onFiles(picked);
  }

  return (
    // The wrapper only listens for drags; the real controls (buttons, inputs) live inside it
    // and stay keyboard-reachable, so no role/tabIndex here.
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      className={`dropzone${over ? " is-over" : ""}${className ? ` ${className}` : ""}`}
      onDragEnter={onDragEnter}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      data-testid="dropzone"
    >
      {children}
      {over && (
        <div className="dropzone-overlay" aria-hidden="true">
          <span>{multiple ? t("dropzone.dropFiles", "Pusti fajlove ovde") : t("dropzone.dropFile", "Pusti fajl ovde")}</span>
        </div>
      )}
    </div>
  );
}

/** `<input accept>` semantics: "image/*", exact MIME, or an extension like ".pdf". */
export function matchesAccept(file: File, accept?: string): boolean {
  if (!accept) return true;
  const type = (file.type || "").toLowerCase();
  const name = file.name.toLowerCase();
  return accept.split(",").map((s) => s.trim().toLowerCase()).filter(Boolean).some((rule) => {
    if (rule.startsWith(".")) return name.endsWith(rule);
    if (rule.endsWith("/*")) return type.startsWith(rule.slice(0, -1));
    return type === rule;
  });
}

/**
 * Mounted once (AppShell): a file dropped anywhere outside a zone must not make
 * the browser navigate away to that file.
 */
export function useGlobalDropGuard() {
  useEffect(() => {
    function block(e: Event) {
      const dt = (e as unknown as { dataTransfer?: DataTransfer }).dataTransfer;
      if (dt && Array.from(dt.types).includes("Files")) e.preventDefault();
    }
    window.addEventListener("dragover", block);
    window.addEventListener("drop", block);
    return () => {
      window.removeEventListener("dragover", block);
      window.removeEventListener("drop", block);
    };
  }, []);
}
